/**
 * The camera's background: kept, blurred, replaced by a colour or by an image
 * of the person's choice. A small on-device model separates the person from
 * the room; these are the pure parts around it.
 */

export type BackgroundMode = 'none' | 'blur' | 'color' | 'image';
export const BACKGROUND_MODES: readonly BackgroundMode[] = ['none', 'blur', 'color', 'image'];

export interface CameraBackground {
  readonly mode: BackgroundMode;
  /** Used by `color` (#rrggbb). */
  readonly color: string;
}

/** Calm backdrops that sit well behind a face (content, not interface colours). */
export const BACKGROUND_COLORS = ['#1f2024', '#3b4252', '#2f4f46', '#d8d3c8', '#efe9df', '#c6f25e'] as const;

export const DEFAULT_CAMERA_BACKGROUND: CameraBackground = { mode: 'none', color: BACKGROUND_COLORS[0] };

/** Input size of the segmentation model (MediaPipe selfie segmentation, landscape). */
export const SEGMENTATION_SIZE = { width: 256, height: 144 } as const;

/** RGBA pixels → the model's input: planar RGB in 0..1. */
export function rgbaToPlanar(rgba: Uint8ClampedArray, pixels: number, out = new Float32Array(pixels * 3)): Float32Array {
  for (let i = 0; i < pixels; i++) {
    out[i] = rgba[i * 4]! / 255;
    out[pixels + i] = rgba[i * 4 + 1]! / 255;
    out[pixels * 2 + i] = rgba[i * 4 + 2]! / 255;
  }
  return out;
}

/**
 * Turns the model's person probabilities into an alpha mask (RGBA, white with
 * alpha). Frames are blended with the previous mask so edges don't flicker,
 * and a soft ramp around 0.5 keeps the outline smooth when scaled up.
 */
export function alphaMask(alphas: Float32Array, previous: Uint8ClampedArray | null, out = new Uint8ClampedArray(alphas.length * 4), carry = 0.35): Uint8ClampedArray {
  for (let i = 0; i < alphas.length; i++) {
    const p = alphas[i]!;
    // Ramp 0.3..0.7 → 0..1: decisive inside and outside, gentle at the edge.
    const ramped = p <= 0.3 ? 0 : p >= 0.7 ? 1 : (p - 0.3) / 0.4;
    const value = ramped * 255;
    const blended = previous ? previous[i * 4 + 3]! * carry + value * (1 - carry) : value;
    out[i * 4] = 255;
    out[i * 4 + 1] = 255;
    out[i * 4 + 2] = 255;
    out[i * 4 + 3] = blended;
  }
  return out;
}

const HEX = /^#[0-9a-f]{6}$/i;

export function parseCameraBackground(value: unknown): CameraBackground {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Partial<Record<keyof CameraBackground, unknown>>;
  return {
    mode: BACKGROUND_MODES.includes(raw.mode as BackgroundMode) ? (raw.mode as BackgroundMode) : DEFAULT_CAMERA_BACKGROUND.mode,
    color: typeof raw.color === 'string' && HEX.test(raw.color) ? raw.color : DEFAULT_CAMERA_BACKGROUND.color,
  };
}

/**
 * What actually gets drawn: an image background needs an image, and without
 * one it falls back to a blur rather than showing the room unasked.
 */
export function effectiveBackground(background: CameraBackground, hasImage: boolean): CameraBackground {
  return background.mode === 'image' && !hasImage ? { ...background, mode: 'blur' } : background;
}
